// Utility functions for managing VAT preferences in cookies

const COOKIE_NAME = 'vat_preferences';

/**
 * Get VAT preferences from cookies
 * @returns {Object} Object with materialId as key and hasVat (boolean) as value
 */
export const getVatPreferences = () => {
  try {
    const cookies = document.cookie.split(';');
    const vatCookie = cookies.find(cookie => cookie.trim().startsWith(`${COOKIE_NAME}=`));

    if (!vatCookie) {
      return {};
    }

    const cookieValue = vatCookie.split('=')[1];
    const decodedValue = decodeURIComponent(cookieValue);
    return JSON.parse(decodedValue);
  } catch (error) {
    console.error('Error reading VAT preferences from cookies:', error);
    return {};
  }
};

/**
 * Save VAT preference for a specific material
 * @param {number} materialId - The material ID
 * @param {boolean} hasVat - Whether VAT should be applied
 */
export const saveVatPreference = (materialId, hasVat) => {
  try {
    const preferences = getVatPreferences();
    preferences[materialId] = hasVat;

    const cookieValue = encodeURIComponent(JSON.stringify(preferences));

    document.cookie = `${COOKIE_NAME}=${cookieValue}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error('Error saving VAT preference to cookies:', error);
  }
};

/**
 * Get VAT preference for a specific material
 * @param {number} materialId - The material ID
 * @returns {boolean|null} The VAT preference, or null if not found
 */
export const getVatPreference = (materialId) => {
  const preferences = getVatPreferences();
  return preferences[materialId] !== undefined ? preferences[materialId] : null;
};

/**
 * Clear all VAT preferences
 */
export const clearVatPreferences = () => {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Bulk save VAT preferences for multiple materials
 * @param {Array} materials - Array of objects with materialId and hasVat properties
 */
export const saveVatPreferences = (materials) => {
  try {
    const preferences = getVatPreferences();

    materials.forEach(material => {
      if (material.materialId !== undefined && material.hasVat !== undefined) {
        preferences[material.materialId] = material.hasVat;
      }
    });

    const cookieValue = encodeURIComponent(JSON.stringify(preferences));

    document.cookie = `${COOKIE_NAME}=${cookieValue}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error('Error saving VAT preferences to cookies:', error);
  }
};
